import React, {useState, useEffect, useLayoutEffect} from 'react';

const DIALOG_MODE = {
  FIXED: 'fixed',
  SCROLL_CONTAINER: 'scroll-container',
};

/**
 * BaseDialog
 * - 画面高さを超える場合、内側だけ縦スクロール
 * - プロップで大きさを少し調整可能
 */
const BaseDialog = ({
  children,
  isVisible,
  onClose,
  maxHeight = 'min(90vh, 720px)',     // 内側ボックスの最大高さ
  maxWidth  = 'min(96vw, 680px)',     // 内側ボックスの最大幅
  padding   = '20px',                 // 内側の余白
  clickOutsideToClose = true,
  dialogMode,
  containerRef,
}) => {
  const [isDialogVisible, setDialogVisible] = useState(isVisible);
  const [containerMetrics, setContainerMetrics] = useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  });

  useEffect(() => {
    setDialogVisible(isVisible);
  }, [isVisible]);

  const resolvedDialogMode = dialogMode || DIALOG_MODE.FIXED;
  const isScrollContainerMode = resolvedDialogMode === DIALOG_MODE.SCROLL_CONTAINER;

  useLayoutEffect(() => {
    if (!isDialogVisible || !isScrollContainerMode) {
      return undefined;
    }

    const container = containerRef?.current;
    if (!container) {
      return undefined;
    }

    const updateContainerMetrics = () => {
      setContainerMetrics({
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
      });
    };

    updateContainerMetrics();
    container.addEventListener('scroll', updateContainerMetrics, { passive: true });
    window.addEventListener('resize', updateContainerMetrics);

    return () => {
      container.removeEventListener('scroll', updateContainerMetrics);
      window.removeEventListener('resize', updateContainerMetrics);
    };
  }, [isDialogVisible, isScrollContainerMode, containerRef]);

  const handleOutsideClick = (e) => {
    if (!clickOutsideToClose) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  const overlayPosition = isScrollContainerMode ? 'absolute' : 'fixed';
  const overlayHeight = isScrollContainerMode
    ? `${Math.max(containerMetrics.scrollHeight, containerMetrics.clientHeight)}px`
    : '100vh';
  const dialogMaxHeight = isScrollContainerMode
    ? `min(${Math.max(containerMetrics.clientHeight - 32, 160)}px, ${maxHeight})`
    : maxHeight;
  const dialogMaxWidth = isScrollContainerMode ? `min(100%, ${maxWidth})` : maxWidth;
  const dialogPositionStyle = isScrollContainerMode
    ? {
        position: 'absolute',
        top: `${containerMetrics.scrollTop + (containerMetrics.clientHeight / 2)}px`,
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }
    : {};

  return (
    <div
      onClick={handleOutsideClick}
      style={{
        display: isDialogVisible ? 'flex' : 'none', // ダイアログを表示・非表示切り替え
        position: overlayPosition, // scroll-container時は親領域内、通常時は画面全体を覆う
        inset : 0,
        width: '100%',
        height: overlayHeight,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', // 背景を黒く透過
        justifyContent: 'center', // 子要素を中央に配置
        alignItems: isScrollContainerMode ? 'flex-start' : 'center',
        zIndex: 1300, // ダイアログを最前面
      }}
    >
      {/* 内側ボックス（Paper相当） */}
      <div
        style={{
          boxSizing: 'border-box',
          backgroundColor: 'white', // ダイアログの背景
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          width: 'auto',
          maxWidth: dialogMaxWidth,         // 横幅を制限
          margin: '0 auto',                     // 横中央揃え
          maxHeight: dialogMaxHeight,              // ★ これで高さを抑える
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...dialogPositionStyle,
        }}
      >
        {/* スクロール領域：中身が溢れたらここが縦スクロールになる */}
        <div
          style={{
            padding,
            overflowY: 'auto',     // ★ ここがスクロール
            overflowX: 'hidden',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export { DIALOG_MODE };
export default BaseDialog;
